<script setup lang="ts">
import { toRef, useId } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDialogFocus } from '~/composables/useDialogFocus'
import { useMmShellInert } from '../base/shellInert'
import MmIcon from '../base/MmIcon.vue'
import LyricsSingAlong from './LyricsSingAlong.vue'
import PlayButton from './PlayButton.vue'

const props = defineProps<{
  modelValue: boolean; title: string; creator: string; hue: number
  sections: Array<{ heading: string; lines: string[] }>
  activeLine: number; seekable: boolean; state: 'idle' | 'playing' | 'loading'
  estimated?: boolean
}>()
const emit = defineEmits<{ 'update:modelValue': [value: boolean]; play: []; seek: [line: number] }>()
const { t } = useI18n()
const id = `lyrics-${useId()}`
const open = toRef(props, 'modelValue')
const close = () => emit('update:modelValue', false)
const { dialog, closeButton, handleKeydown } = useDialogFocus(open, close)
useMmShellInert(open)
</script>

<template>
  <Teleport to="body">
    <section v-if="modelValue" ref="dialog" class="takeover mm-root t24b-page" :style="{ '--live-h': hue }"
      role="dialog" aria-modal="true" :aria-labelledby="id" tabindex="-1" @keydown="handleKeydown">
      <header class="takeover-head">
        <div><h2 :id="id">{{ title }}</h2><p>@{{ creator }}</p></div>
        <button ref="closeButton" class="icon-btn" type="button" :aria-label="t('mm.song.closeLyrics', { title })" @click="close"><MmIcon name="x" :size="24" /></button>
      </header>
      <LyricsSingAlong :sections="sections" :active-line="activeLine" :seekable="seekable" :estimated="estimated" takeover @seek="emit('seek', $event)" />
      <footer class="takeover-controls">
        <PlayButton :title="title" :state="state" size="lg" cancelable :label="state === 'loading' ? `${t('ux.cancelPlayback')} ${title}` : undefined" @play="emit('play')" />
        <span class="t-mono takeover-hint">{{ t('t24b.jumpLine') }}</span>
      </footer>
    </section>
  </Teleport>
</template>

<style scoped>
.takeover { position: fixed; inset: 0; z-index: var(--z-sheet); display: grid; grid-template-rows: auto minmax(0, 1fr) auto; padding: var(--s-4) clamp(16px, 5vw, 64px) calc(var(--s-6) + var(--safe-b)); background: var(--song-takeover-bg); --takeover-ink: var(--song-takeover-ink); color: var(--takeover-ink); animation: lyrics-take-in var(--d-slow) var(--ease-out); }
.takeover-head, .takeover-controls { display: flex; align-items: center; justify-content: space-between; gap: var(--s-4); }
.takeover-head > div { min-width: 0; }
.takeover-head h2 { margin: 0; font: 700 16px/1.2 var(--font-display); overflow-wrap: anywhere; }
.takeover-head p { margin: 0; opacity: .7; font-size: 14px; }
.takeover .icon-btn { color: var(--takeover-ink); flex: none; }
.takeover .icon-btn:hover { background: var(--live-soft); }
.takeover-controls p { max-width: 36ch; font: 500 var(--t-micro)/1.6 var(--font-mono); }
.takeover :deep(.play) { background: var(--takeover-ink); color: var(--song-takeover-play); flex: none; }
@keyframes lyrics-take-in { from { clip-path: circle(0% at 90% 10%); } to { clip-path: circle(150% at 90% 10%); } }
.takeover-hint { opacity: .7; font-size: 13px; }
@media(prefers-reduced-motion:reduce) { .takeover { animation: none; } }
</style>
